/**
 * Exception UI: severity text → icon, colors, tags (single mapping table).
 * Type titles and descriptions live in shipment-exception-catalog.ts.
 */

import {
  exceptionFlagsToSeverities,
  normalizeExceptionSeverity,
  type ShipmentExceptionSeverity,
} from '@/utils/shipment-exception-flags';

export {
  exceptionListCardTitle,
  exceptionTypeDescription,
  exceptionTypeTitle as displayExceptionEventTitle,
} from '@/utils/shipment-exception-catalog';

export type ExceptionSeverityIconDisplay = {
  severity: ShipmentExceptionSeverity;
  icon: string;
  colorClass: string;
  accentBarClass: string;
  tagLabel: string;
  /** Fill for map markers — Tailwind 600 of {@link colorClass}. */
  mapFillHex: string;
};

const SEVERITY_ICON_DISPLAY: Record<
  ShipmentExceptionSeverity,
  Omit<ExceptionSeverityIconDisplay, 'severity'>
> = {
  critical: {
    icon: 'pi-exclamation-triangle',
    colorClass: 'text-red-600',
    accentBarClass: 'bg-red-600',
    tagLabel: 'High',
    mapFillHex: '#dc2626',
  },
  warning: {
    icon: 'pi-exclamation-triangle',
    colorClass: 'text-amber-600',
    accentBarClass: 'bg-amber-500',
    tagLabel: 'Medium',
    mapFillHex: '#d97706',
  },
  info: {
    icon: 'pi-exclamation-triangle',
    colorClass: 'text-sky-600',
    accentBarClass: 'bg-sky-500',
    tagLabel: 'Low',
    mapFillHex: '#0284c7',
  },
};

/** Map event / API severity string → icon + colors (detail cards, list). */
export function exceptionIconDisplayForSeverity(
  severity: string,
): ExceptionSeverityIconDisplay | null {
  const key = normalizeExceptionSeverity(severity);
  if (!key) {
    return null;
  }
  return {
    severity: key,
    ...SEVERITY_ICON_DISPLAY[key],
  };
}

/** Shipment list: flags → severity strings → icon display rows. */
export function exceptionIconDisplaysFromFlags(
  flags: number | null | undefined,
): ExceptionSeverityIconDisplay[] {
  return exceptionFlagsToSeverities(flags).map((severity) => ({
    severity,
    ...SEVERITY_ICON_DISPLAY[severity],
  }));
}

const MAP_EXCEPTION_ICON_CLASS: Record<ShipmentExceptionSeverity, string> = {
  critical: 'gpx-shipment-map-exception-icon--critical',
  warning: 'gpx-shipment-map-exception-icon--warning',
  info: 'gpx-shipment-map-exception-icon--info',
};

/**
 * Map popup HTML: same PrimeIcons as {@link ShipmentExceptionFlagsIcons} in the list table.
 */
export function exceptionIconsHtmlFromFlags(
  flags: number | null | undefined,
): string {
  const items = exceptionIconDisplaysFromFlags(flags);
  if (items.length === 0) {
    return '';
  }

  const icons = items
    .map((item) => {
      const title = escapeHtmlAttribute(item.tagLabel);
      return `<i class="pi ${item.icon} gpx-shipment-map-exception-icon ${MAP_EXCEPTION_ICON_CLASS[item.severity]}" title="${title}" aria-label="${title}"></i>`;
    })
    .join('');

  return `<div class="gpx-shipment-map-tooltip__exceptions" role="list" aria-label="Shipment exceptions">${icons}</div>`;
}

function escapeHtmlAttribute(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function exceptionHeaderIconClass(severity: string): string {
  return (
    exceptionIconDisplayForSeverity(severity)?.colorClass ?? 'text-neutral-600'
  );
}

export function exceptionAccentBarClass(severity: string): string {
  return (
    exceptionIconDisplayForSeverity(severity)?.accentBarClass ??
    'bg-neutral-400'
  );
}

export function exceptionSeverityLabel(severity: string): string {
  return exceptionIconDisplayForSeverity(severity)?.tagLabel ?? severity;
}
